import React, { useMemo } from 'react';

import {
    IconBriefcase2,
    IconBrowser,
    IconCurrencyEuro,
    IconDeviceDesktop,
    IconFileAnalytics,
    IconMapPin,
    IconMessages,
    IconPercentage,
    IconSettings,
    IconShoppingCart,
    IconStar,
    IconTemplate,
    IconTicket,
    IconUserSquareRounded,
    IconUsers,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { CAPABILITY } from '@/constants/capabilities';
import { ROUTES } from '@/constants/routes';
import { useAccessPermissions } from '@/hooks/useAccessPermissions';

import { GeneralStats } from '../../types/general-stats';
import { SidebarMenuArea } from './SidebarMenuListItem.types';

export const useGetSidebarMenuAreas = (statisticsSidebar: Partial<GeneralStats>): SidebarMenuArea[] => {
    const { t } = useTranslation();
    const { checkAccess } = useAccessPermissions();

    const controlPanelArea: SidebarMenuArea = useMemo(
        () => [
            {
                id: 'home',
                pathname: ROUTES.root.route,
                text: t('dataPanel'),
                icon: <IconDeviceDesktop size={19} />,
            },
        ],
        [t],
    );

    const linksArea1: SidebarMenuArea = useMemo(
        () =>
            [
                {
                    id: 'subscriptions',
                    pathname: ROUTES.subscriptions.route,
                    text: t('subscriptions'),
                    icon: (
                        <span style={{ transform: 'rotate(90deg) translateY(.333rem)' }}>
                            <IconTicket size={19} />
                        </span>
                    ),
                    statistics: statisticsSidebar?.subscriptions,
                    allowedCapabilities: [CAPABILITY.SUBSCRIPTION_VIEW, CAPABILITY.SUBSCRIPTION_TYPE_VIEW],
                },
                {
                    id: 'enclosures',
                    pathname: ROUTES.enclosures.route,
                    text: t('enclosures'),
                    icon: <IconMapPin size={19} />,
                    statistics: statisticsSidebar?.enclosures,
                    allowedCapabilities: [CAPABILITY.ENCLOSURE_VIEW],
                },
                {
                    id: 'events',
                    pathname: ROUTES.events.route,
                    text: t('events'),
                    icon: <IconStar size={19} />,
                    statistics: statisticsSidebar?.events,
                    warnings: statisticsSidebar?.eventSessionWithErrors
                        ? t('thereAreSessionsWithoutCodes', { count: statisticsSidebar?.eventSessionWithErrors })
                        : undefined,
                    allowedCapabilities: [CAPABILITY.EVENT_VIEW],
                },
                {
                    id: 'discounts',
                    pathname: ROUTES.discounts.route,
                    text: t('discountCodes'),
                    icon: <IconPercentage size={19} />,
                    allowedCapabilities: [CAPABILITY.DISCOUNT_VIEW],
                },
            ].filter(
                ({ allowedCapabilities }) =>
                    !allowedCapabilities ||
                    allowedCapabilities.length === 0 ||
                    allowedCapabilities.some((allowedCapability) => checkAccess(allowedCapability)),
            ),
        [
            t,
            checkAccess,
            statisticsSidebar?.enclosures,
            statisticsSidebar?.eventSessionWithErrors,
            statisticsSidebar?.events,
            statisticsSidebar?.subscriptions,
        ],
    );

    const linksArea2: SidebarMenuArea = useMemo(
        () =>
            [
                {
                    id: 'subscribers',
                    pathname: ROUTES.subscribers.route,
                    text: t('subscribers'),
                    icon: <IconUsers size={19} />,
                    statistics: statisticsSidebar?.subscribers,
                    allowedCapabilities: [CAPABILITY.SUBSCRIBER_VIEW],
                },
                {
                    id: 'companies',
                    pathname: ROUTES.companies.route,
                    text: t('companies'),
                    icon: <IconBriefcase2 size={19} />,
                    statistics: statisticsSidebar?.companies,
                    allowedCapabilities: [CAPABILITY.COMPANY_VIEW],
                },
                {
                    id: 'customerService',
                    pathname: ROUTES.customerService.route,
                    text: t('customerService'),
                    icon: <IconMessages size={19} />,
                    statistics: statisticsSidebar?.incidences,
                    allowedCapabilities: [CAPABILITY.INCIDENCE_VIEW, CAPABILITY.NOTIFICATION_VIEW],
                },
            ].filter(
                ({ allowedCapabilities }) =>
                    !allowedCapabilities ||
                    allowedCapabilities.length === 0 ||
                    allowedCapabilities.some((allowedCapability) => checkAccess(allowedCapability)),
            ),
        [t, checkAccess, statisticsSidebar?.companies, statisticsSidebar?.incidences, statisticsSidebar?.subscribers],
    );

    const linksArea3: SidebarMenuArea = useMemo(
        () =>
            [
                {
                    id: 'operations',
                    pathname: ROUTES.operations.route,
                    text: t('operations'),
                    icon: <IconCurrencyEuro size={19} />,
                    allowedCapabilities: [CAPABILITY.OPERATION_VIEW],
                },
                {
                    id: 'settlements',
                    pathname: ROUTES.settlements.route,
                    text: t('settlements'),
                    icon: <IconShoppingCart size={19} />,
                },
                {
                    id: 'reports',
                    pathname: ROUTES.reports.route,
                    text: t('reports'),
                    icon: <IconFileAnalytics size={19} />,
                    allowedCapabilities: [CAPABILITY.REPORT_VIEW],
                },
            ].filter(
                ({ allowedCapabilities }) =>
                    !allowedCapabilities ||
                    allowedCapabilities.length === 0 ||
                    allowedCapabilities.some((allowedCapability) => checkAccess(allowedCapability)),
            ),
        [t, checkAccess],
    );

    const linksArea4: SidebarMenuArea = useMemo(
        () =>
            [
                {
                    id: 'userManagement',
                    pathname: ROUTES.userManagement.route,
                    text: t('userManagement'),
                    icon: <IconUserSquareRounded size={19} />,
                    allowedCapabilities: [CAPABILITY.USER_VIEW, CAPABILITY.ROLE_VIEW],
                },
                {
                    id: 'webpages',
                    pathname: ROUTES.webpages.route,
                    text: t('webEdition'),
                    icon: <IconBrowser size={19} />,
                    allowedCapabilities: [CAPABILITY.CMS_VIEW],
                },
                {
                    id: 'templates',
                    pathname: ROUTES.templates.route,
                    text: t('generalTemplates'),
                    icon: <IconTemplate size={19} />,
                    allowedCapabilities: [CAPABILITY.TEMPLATE_VIEW],
                },
                {
                    id: 'settings',
                    pathname: ROUTES.settings.route,
                    text: t('settings'),
                    icon: <IconSettings size={19} />,
                },
            ].filter(
                ({ allowedCapabilities }) =>
                    !allowedCapabilities ||
                    allowedCapabilities.length === 0 ||
                    allowedCapabilities.some((allowedCapability) => checkAccess(allowedCapability)),
            ),
        [t, checkAccess],
    );

    return useMemo(() => {
        const sidebarItems: SidebarMenuArea[] = [];

        if (controlPanelArea.length > 0) sidebarItems.push(controlPanelArea);
        if (linksArea1.length > 0) sidebarItems.push(linksArea1);
        if (linksArea2.length > 0) sidebarItems.push(linksArea2);
        if (linksArea3.length > 0) sidebarItems.push(linksArea3);
        if (linksArea4.length > 0) sidebarItems.push(linksArea4);

        return sidebarItems;
    }, [controlPanelArea, linksArea1, linksArea2, linksArea3, linksArea4]);
};
